import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NavigationBar from '../components/NavigationBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, ArrowLeft, Clock, HelpCircle, Smile, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

// Популярные эмодзи для быстрого доступа
const EMOJI_LIST = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍',
  '🥰', '😘', '😗', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤭', '🤫', '🤔',
  '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '🤯', '😴',
  '🥳', '🤩', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', '😲',
  '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💕', '💞', '💓',
  '💗', '💖', '💘', '💝', '💟', '❣️', '💔', '🔥', '✨', '🌟', '💫', '⭐',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👋', '🖐️', '✋', '👏',
  '🙌', '👐', '🤲', '🤝', '🙏', '💪', '🦾', '🖕', '✍️', '🤳', '💅', '🦵'
];

const Chat = () => {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [matchInfo, setMatchInfo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [userScrolled, setUserScrolled] = useState(false);
  const lastMessageCountRef = useRef(0);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const chatClosedRef = useRef(false);

  useEffect(() => {
    loadChatData();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    if (messages.length > lastMessageCountRef.current && !userScrolled) {
      scrollToBottom();
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, userScrolled]);

  // Закрытие emoji picker при клике вне
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleScroll = (e) => {
    const container = e.target;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    setUserScrolled(!isAtBottom);
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const redirectIfChatRemoved = (error) => {
    const statusCode = error?.response?.status;
    const errorMessage = error?.response?.data?.detail || '';
    const chatRemoved = statusCode === 404 || statusCode === 410 || errorMessage.toLowerCase().includes('expired');

    if (!chatRemoved || chatClosedRef.current) {
      return false;
    }

    chatClosedRef.current = true;
    toast.error('Срок чата истёк, чат удалён');
    navigate('/matches');
    return true;
  };

  const loadChatData = async () => {
    try {
      const [infoResponse, messagesResponse] = await Promise.all([
        api.get(`/chat/${matchId}/info`),
        api.get(`/chat/${matchId}/messages`)
      ]);
      
      setMatchInfo(infoResponse.data);
      setMessages(messagesResponse.data);
      lastMessageCountRef.current = messagesResponse.data.length;
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      if (!redirectIfChatRemoved(error)) {
        toast.error(error?.response?.data?.detail || 'Ошибка загрузки чата');
        navigate('/matches');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await api.get(`/chat/${matchId}/messages`);
      setMessages(response.data);
    } catch (error) {
      if (redirectIfChatRemoved(error)) {
        return;
      }
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setUserScrolled(false);
    try {
      const response = await api.post(`/chat/${matchId}/message`, {
        text: newMessage
      });
      
      setMessages([...messages, response.data]);
      setNewMessage('');
      setTimeout(scrollToBottom, 50);
    } catch (error) {
      if (!redirectIfChatRemoved(error)) {
        toast.error(error.response?.data?.detail || 'Ошибка отправки');
      }
    } finally {
      setSending(false);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    inputRef.current?.focus();
  };

  const openPhotoGallery = (index = 0) => {
    setCurrentPhotoIndex(index);
    setShowPhotoGallery(true);
  };

  const nextPhoto = () => {
    const photos = matchInfo?.partner?.photos || [];
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    const photos = matchInfo?.partner?.photos || [];
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const getPartnerAvatar = () => {
    if (matchInfo?.partner?.photos?.length > 0) {
      return matchInfo.partner.photos[0];
    }
    return null;
  };

  const getEducationText = (education) => {
    const map = { higher: 'Высшее', secondary: 'Среднее', vocational: 'Средне-специальное' };
    return map[education] || education;
  };

  const getSmokingText = (smoking) => {
    const map = { negative: 'Отрицательное', positive: 'Позитивное', neutral: 'Нейтральное', any: 'Неважно' };
    return map[smoking] || smoking;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-[#7A7A7A]">Загрузка...</p>
        </div>
      </div>
    );
  }

  const partnerAvatar = getPartnerAvatar();
  const partner = matchInfo?.partner;
  const partnerPhotos = partner?.photos || [];

  return (
    <div className="fixed inset-0 flex flex-col bg-[#F0F2F5]">
      {/* Fixed Navigation - stays at top always */}
      <div className="flex-shrink-0 sticky top-0 z-50 bg-white">
        <NavigationBar />
      </div>
      
      {/* Main Chat Container with border */}
      <div className="flex-1 flex flex-col max-w-4xl w-full mx-auto my-4 bg-white rounded-2xl shadow-lg border border-[#E5E5E5] overflow-hidden">
        
        {/* Chat Header */}
        <div className="flex-shrink-0 border-b border-[#E5E5E5] px-4 py-3 bg-gradient-to-r from-white to-[#F8F9FA]">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/matches')} 
              data-testid="back-to-matches"
              className="flex items-center gap-2 text-[#1A73E8] hover:text-[#1557B5] transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium hidden sm:inline">Назад</span>
            </button>
            
            <div className="h-8 w-px bg-[#E5E5E5]"></div>
            
            <button 
              onClick={() => setShowProfileModal(true)}
              className="flex items-center gap-3 flex-1 min-w-0 hover:bg-[#F6F7F9] rounded-lg p-2 transition-colors"
            >
              {partnerAvatar ? (
                <img 
                  src={partnerAvatar} 
                  alt={partner?.name}
                  className="w-11 h-11 rounded-full object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity border-2 border-[#1A73E8]/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPhotoGallery(0);
                  }}
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#7B61FF] to-[#E056FD] flex items-center justify-center text-white font-bold flex-shrink-0 border-2 border-white shadow">
                  {partner?.name?.[0]}
                </div>
              )}
              <div className="text-left min-w-0">
                <h2 className="text-base font-semibold text-[#1F1F1F] truncate">
                  {partner?.name}, {partner?.age}
                </h2>
                <div className="flex items-center gap-1 text-[#FF5757] text-xs">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">Чат удалится через {matchInfo?.expires_in_days} дней</span>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInfoModal(true);
                    }}
                    className="ml-1 text-[#7A7A7A] hover:text-[#1A73E8] transition-colors flex-shrink-0"
                  >
                    <HelpCircle className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Scrollable Messages Area */}
        <div 
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto bg-[#F8F9FA]"
          data-testid="messages-container"
          style={{ 
            backgroundImage: 'radial-gradient(circle at 25px 25px, #E5E5E5 2%, transparent 0%), radial-gradient(circle at 75px 75px, #E5E5E5 2%, transparent 0%)',
            backgroundSize: '100px 100px'
          }}
        >
          <div className="p-4 pb-2">
            <div className="space-y-3">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-block bg-white px-6 py-4 rounded-2xl shadow-sm border border-[#E5E5E5]">
                    <p className="text-[#7A7A7A]">💬 Начните переписку первым!</p>
                  </div>
                </div>
              ) : (
                messages.map((message) => {
                  const isOwn = message.sender_id === user.id;
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${message.id}`}
                    >
                      {!isOwn && (
                        <div className="flex-shrink-0 mr-2">
                          {partnerAvatar ? (
                            <img 
                              src={partnerAvatar} 
                              alt=""
                              className="w-8 h-8 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity border border-[#E5E5E5]"
                              onClick={() => openPhotoGallery(0)}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7B61FF] to-[#E056FD] flex items-center justify-center text-white text-sm font-bold">
                              {partner?.name?.[0]}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div
                        className={`max-w-[70%] px-4 py-2.5 rounded-2xl shadow-sm ${
                          isOwn
                            ? 'bg-gradient-to-br from-[#1A73E8] to-[#4A90E8] text-white rounded-tr-md'
                            : 'bg-white text-[#1F1F1F] border border-[#E5E5E5] rounded-tl-md'
                        }`}
                      >
                        <p className="break-words text-sm leading-relaxed">{message.text}</p>
                        <p className={`text-xs mt-1.5 ${isOwn ? 'text-white/70' : 'text-[#7A7A7A]'}`}>
                          {new Date(message.timestamp).toLocaleTimeString('ru-RU', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

        {/* Input Area */}
        <div className="flex-shrink-0 border-t border-[#E5E5E5] p-3 bg-white">
          <form onSubmit={handleSendMessage} className="flex gap-2 items-center relative">
            {/* Emoji Picker */}
            <div className="relative" ref={emojiPickerRef}>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-2.5 text-[#7A7A7A] hover:text-[#1A73E8] hover:bg-[#F6F7F9] rounded-full transition-colors"
                data-testid="emoji-button"
              >
                <Smile className="w-6 h-6" />
              </button>
              
              {showEmojiPicker && (
                <div className="absolute bottom-14 left-0 bg-white border border-[#E5E5E5] rounded-xl shadow-xl p-3 w-80 max-h-72 overflow-y-auto z-50">
                  <div className="grid grid-cols-9 gap-1">
                    {EMOJI_LIST.map((emoji, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleEmojiSelect(emoji)}
                        className="w-8 h-8 flex items-center justify-center text-xl hover:bg-[#F6F7F9] rounded transition-colors hover:scale-125"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Введите сообщение..."
              className="flex-1 rounded-full border-[#E5E5E5] focus:border-[#1A73E8] py-3 px-4"
              disabled={sending}
              data-testid="message-input"
            />
            <Button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="px-5 py-3 rounded-full"
              style={{ background: 'linear-gradient(135deg, #1A73E8 0%, #4A90E8 100%)' }}
              data-testid="send-message-button"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </div>

      {/* Info Modal */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center" style={{ color: '#1F1F1F' }}>
              Информация о чате
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-[#7A7A7A]">
              Чат будет доступен для общения 30 дней, после этого времени будет удалён.
            </p>
          </div>
          <Button
            onClick={() => setShowInfoModal(false)}
            className="w-full py-4 rounded-full text-white font-semibold"
            style={{ background: '#1A73E8' }}
          >
            ПОНЯТНО
          </Button>
        </DialogContent>
      </Dialog>

      {/* Profile Modal with full info */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center" style={{ color: '#1F1F1F' }}>
              Профиль собеседника
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Avatar - clickable for gallery */}
            <div className="flex justify-center mb-4">
              {partnerAvatar ? (
                <img 
                  src={partnerAvatar} 
                  alt={partner?.name}
                  className="w-24 h-24 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    setShowProfileModal(false);
                    openPhotoGallery(0);
                  }}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#7B61FF] to-[#E056FD] flex items-center justify-center text-white text-4xl font-bold">
                  {partner?.name?.[0]}
                </div>
              )}
            </div>
            
            {/* Name and Age */}
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-[#1F1F1F]">
                {partner?.name}, {partner?.age}
              </h3>
              {partner?.city && (
                <p className="text-[#7A7A7A]">{partner.city}</p>
              )}
            </div>

            {/* Profile Details */}
            <div className="space-y-3 bg-[#F6F7F9] rounded-xl p-4">
              {partner?.height && (
                <div className="flex justify-between">
                  <span className="text-[#7A7A7A]">Рост:</span>
                  <span className="text-[#1F1F1F] font-medium">{partner.height} см</span>
                </div>
              )}
              {partner?.weight && (
                <div className="flex justify-between">
                  <span className="text-[#7A7A7A]">Вес:</span>
                  <span className="text-[#1F1F1F] font-medium">{partner.weight} кг</span>
                </div>
              )}
              {partner?.gender && (
                <div className="flex justify-between">
                  <span className="text-[#7A7A7A]">Пол:</span>
                  <span className="text-[#1F1F1F] font-medium">
                    {partner.gender === 'male' ? 'Мужской' : 'Женский'}
                  </span>
                </div>
              )}
              {partner?.education && (
                <div className="flex justify-between">
                  <span className="text-[#7A7A7A]">Образование:</span>
                  <span className="text-[#1F1F1F] font-medium">{getEducationText(partner.education)}</span>
                </div>
              )}
              {partner?.smoking && (
                <div className="flex justify-between">
                  <span className="text-[#7A7A7A]">Курение:</span>
                  <span className="text-[#1F1F1F] font-medium">{getSmokingText(partner.smoking)}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {partner?.description && (
              <div className="mt-4">
                <p className="text-sm text-[#7A7A7A] mb-2">О себе:</p>
                <p className="text-sm text-[#1F1F1F] bg-[#F6F7F9] p-3 rounded-lg">
                  {partner.description}
                </p>
              </div>
            )}

            {/* Photos - clickable thumbnails */}
            {partnerPhotos.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-[#7A7A7A] mb-2">Фотографии ({partnerPhotos.length}):</p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {partnerPhotos.map((photo, idx) => (
                    <img 
                      key={idx}
                      src={photo}
                      alt=""
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity border-2 border-transparent hover:border-[#1A73E8]"
                      onClick={() => {
                        setShowProfileModal(false);
                        openPhotoGallery(idx);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <Button
            onClick={() => setShowProfileModal(false)}
            variant="outline"
            className="w-full"
          >
            Закрыть
          </Button>
        </DialogContent>
      </Dialog>

      {/* Full-screen Photo Gallery */}
      <Dialog open={showPhotoGallery} onOpenChange={setShowPhotoGallery}>
        <DialogContent className="max-w-4xl w-full h-[90vh] p-0 bg-black/95 border-none [&>button]:hidden">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setShowPhotoGallery(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {/* Photo counter */}
            <div className="absolute top-4 left-4 z-10 px-3 py-1 bg-white/10 rounded-full text-white text-sm">
              {currentPhotoIndex + 1} / {partnerPhotos.length}
            </div>

            {/* Previous button */}
            {partnerPhotos.length > 1 && (
              <button
                onClick={prevPhoto}
                className="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Photo */}
            {partnerPhotos[currentPhotoIndex] && (
              <img
                src={partnerPhotos[currentPhotoIndex]}
                alt=""
                className="max-w-full max-h-full object-contain"
              />
            )}

            {/* Next button */}
            {partnerPhotos.length > 1 && (
              <button
                onClick={nextPhoto}
                className="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Thumbnail navigation */}
            {partnerPhotos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {partnerPhotos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPhotoIndex(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentPhotoIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
